/**
 * Ämnesstyrd internlänkning ("Relaterat på sidan").
 *
 * Syfte: varje djup sida ska ha 4–6 kontextuella länkar till sidor som faktiskt
 * hör till samma ämne — inte en generisk länklista. Det bygger topical clusters,
 * sprider länkvärde till hubbarna och minskar antalet orphan-sidor.
 *
 * Reglerna matchas mot sidans egen text (rubrik + nyckelord + brödtext).
 * Urvalet är deterministiskt (hash på sidans nyckel) så samma sida alltid får
 * samma länkar — ingen hydration-mismatch och stabilt för crawlers.
 *
 * ALLA hrefs nedan måste peka på befintliga rutter i src/routes.
 */

import { hashString } from "@/lib/kb-blocks";

export type RelatedLink = {
  href: string;
  label: string;
  /** Kort motivering som visas under länken. */
  note: string;
};

type Rule = RelatedLink & {
  /** Ämnesmatchning mot sidans text. */
  test: RegExp;
  /** Högre vikt = väljs först. */
  weight: number;
};

/** Länkar som alltid är relevanta för en husbils-/husvagnssida. */
const EVERGREEN: RelatedLink[] = [
  { href: "/fordon", label: "Fordon i lager", note: "Aktuella husbilar och husvagnar i Kalix" },
  { href: "/kunskap", label: "Kunskapsbanken", note: "Alla djupguider samlade" },
  { href: "/guider", label: "Guider", note: "Steg-för-steg inför köp, service och resa" },
];

const RULES: Rule[] = [
  // Pris, värde och köp/sälj
  {
    href: "/vardera-husbil",
    label: "Värdera din husbil",
    note: "Marknadsvärde på 2 minuter, baserat på riktiga annonspriser",
    test: /pris|v[äa]rde|v[äa]rdering|s[äa]lja|byta in|marknad|beg?agnad|inbyte/i,
    weight: 100,
  },
  {
    href: "/prisstatistik-husbil",
    label: "Prisstatistik husbil",
    note: "Snittpriser per årsmodell och segment",
    test: /pris|prisl[äa]ge|marknad|statistik|kosta|budget|v[äa]rdeminskning/i,
    weight: 92,
  },
  {
    href: "/marknad",
    label: "Marknadsrummet",
    note: "Prisindex, statistik, börsen och värdering samlat",
    test: /marknad|pris|index|statistik|v[äa]rde|kosta|b[öo]rs/i,
    weight: 86,
  },
  {
    href: "/borsen",
    label: "Fritidsbörsen",
    note: "Kurs och utveckling för bolagen bakom husbilsmärkena",
    test: /b[öo]rs|aktie|kabe|trigano|knaus|thor|hymer|dometic|thule|bolag|marknad/i,
    weight: 78,
  },
  {
    href: "/prisindex",
    label: "AK Prisindex",
    note: "Prisutvecklingen månad för månad, plus vad tillbehören kostar",
    test: /pris|prisutveckling|index|marknad|kosta|tillbeh[öo]r|budget|v[äa]rdeminskning/i,
    weight: 90,
  },
  {
    href: "/salja-husbil",
    label: "Sälja husbil",
    note: "Förmedling, inbyte eller privat – vad ger mest?",
    test: /s[äa]lja|f[öo]rmedling|annons|inbyte|byta upp/i,
    weight: 88,
  },
  {
    href: "/salda",
    label: "Sålda fordon",
    note: "Vad fordon faktiskt gick för hos oss",
    test: /s[åa]lt|s[åa]lda|slutpris|marknad|prisl[äa]ge/i,
    weight: 70,
  },
  // Ekonomi och kalkyler
  {
    href: "/verktyg/manadskostnad-husbil",
    label: "Månadskostnad – kalkylator",
    note: "Lån, försäkring, skatt och service i en summa",
    test: /l[åa]n|finansiering|r[äa]nta|m[åa]nadskostnad|avbetalning|leasing|driftkostnad|kostnad/i,
    weight: 95,
  },
  {
    href: "/leasa-husbil",
    label: "Leasa husbil – går det, och vad kostar det?",
    note: "Privatleasing, företagsleasing, avbetalning och momsen",
    test: /leas|privatleasing|finansiering|avbetalning|kontantinsats|moms|f[öo]retag|r[äa]nta|restv[äa]rde/i,
    weight: 92,
  },
  {
    href: "/verktyg/avskrivning-husbil",
    label: "Avskrivning & värdekurva",
    note: "Så mycket tappar olika årsmodeller",
    test: /avskrivning|v[äa]rdeminskning|[äa]ga|totalkostnad|restv[äa]rde/i,
    weight: 85,
  },
  {
    href: "/verktyg/bransleforbrukning-husbil",
    label: "Bränsleförbrukning",
    note: "Räkna ut dieselkostnaden för din resa",
    test: /br[äa]nsle|diesel|f[öo]rbrukning|liter|mil|resa|milkostnad/i,
    weight: 82,
  },
  {
    href: "/husbilsforsakring",
    label: "Husbilsförsäkring",
    note: "Vad som faktiskt påverkar premien",
    test: /f[öo]rs[äa]kring|premie|halvf[öo]rs[äa]kring|helf[öo]rs[äa]kring|sj[äa]lvrisk|stold|st[öo]ld/i,
    weight: 80,
  },
  // Körkort, vikt och mått
  {
    href: "/verktyg/korkort-husbil",
    label: "Körkortskoll",
    note: "Får du köra bilen med ditt körkort?",
    test: /k[öo]rkort|b96|be|3\s?500|totalvikt|tj[äa]nstevikt|lastvikt|[öo]verlast/i,
    weight: 94,
  },
  {
    href: "/b96-korkort",
    label: "B96 – hela guiden",
    note: "Utbildning, kostnad och vad du får dra",
    test: /b96|sl[äa]p|dragvikt|husvagn.*k[öo]rkort|k[öo]rkort.*husvagn/i,
    weight: 86,
  },
  {
    href: "/verktyg/passar-den-hos-mig",
    label: "Passar den hos mig?",
    note: "Mät garaget, tomten och svängrummet i AR",
    test: /m[åa]tt|l[äa]ngd|bredd|h[öo]jd|garage|uppst[äa]llning|parkera|sv[äa]ng|carport|tomt/i,
    weight: 90,
  },
  // Teknik och underhåll
  {
    href: "/fukttest-husbil",
    label: "Fukttest",
    note: "Hur en fuktmätning görs och vad värdena betyder",
    test: /fukt|v[äa]ta|m[öo]gel|l[äa]cka|kondens|tak|list|golv/i,
    weight: 96,
  },
  {
    href: "/besiktning-husbil",
    label: "Besiktning",
    note: "Vanliga anmärkningar och hur du slipper dem",
    test: /besiktning|kontrollbesiktning|anm[äa]rkning|bilprovning|underrede|broms/i,
    weight: 88,
  },
  {
    href: "/dieselvarmare-husbil",
    label: "Dieselvärmare",
    note: "Truma, Webasto och drift i minusgrader",
    test: /v[äa]rmare|dieselv[äa]rmare|webasto|truma|gasolv[äa]rme|v[äa]rmesystem|vinter|kyla/i,
    weight: 86,
  },
  {
    href: "/solceller-husbil",
    label: "Solceller",
    note: "Dimensionera panel, regulator och batteri",
    test: /solcell|solpanel|batteri|litium|el|amper|watt|str[öo]m|laddning|regulator/i,
    weight: 90,
  },
  {
    href: "/husbilsdack",
    label: "Husbilsdäck",
    note: "CP-märkning, ålder och rätt lufttryck",
    test: /d[äa]ck|lufttryck|cp-m[äa]rk|vinterd[äa]ck|m[öo]nsterdjup|f[äa]lg/i,
    weight: 88,
  },
  {
    href: "/tvatta-husvagn",
    label: "Tvätta husvagn & husbil",
    note: "Rätt medel för plast, gelcoat och tak",
    test: /tv[äa]tt|reng[öo]ring|polera|gelcoat|oxid|vax|gr[öo]nbel[äa]ggning/i,
    weight: 78,
  },
  {
    href: "/boka-service",
    label: "Boka verkstad",
    note: "Fukttest, gasolkontroll och service i Kalix",
    test: /service|verkstad|reparation|fukt|gasol|besiktning|solceller|f[öo]rvaring|montering/i,
    weight: 84,
  },
  {
    href: "/reservdelar",
    label: "Reservdelar",
    note: "Originaldelar till Rapido och fler märken",
    test: /reservdel|del|trasig|byta ut|pump|lucka|f[öo]nster|k[üu]hlschrank|kylsk[åa]p/i,
    weight: 80,
  },
  {
    href: "/butik",
    label: "Butiken",
    note: "Utrustning vi själva använder på resan",
    test: /utrustning|tillbeh[öo]r|k[öo]pa till|matta|kabel|slang|grill|kylbox|niv[åa]|toalett/i,
    weight: 72,
  },
  // Resa, ställplatser, community
  {
    href: "/stallplatser-husbil",
    label: "Ställplatser",
    note: "Var du får stå – och vad det kostar",
    test: /st[äa]llplats|camping|[öo]vernatta|parkera|allemansr[äa]tt|natt/i,
    weight: 90,
  },
  {
    href: "/husbil-resa",
    label: "Reseplaneraren",
    note: "Få en färdig rutt anpassad efter din bil",
    test: /resa|rutt|semester|tur|norge|lofoten|road ?trip|planera|etapp/i,
    weight: 86,
  },
  {
    href: "/campingtips",
    label: "Campingtips",
    note: "Platser och tips från oss och våra kunder",
    test: /camping|tips|plats|sj[öo]|fiske|norrbotten|midnattssol/i,
    weight: 74,
  },
  {
    href: "/forum",
    label: "Fritidsforum",
    note: "Fråga andra ägare och dela erfarenheter",
    test: /fr[åa]ga|erfarenhet|diskussion|andra [äa]gare|forum|community|r[åa]d/i,
    weight: 68,
  },
  {
    href: "/erfarenheter",
    label: "Erfarenhetsrapporter",
    note: "Långtidsintryck från riktiga ägare",
    test: /erfarenhet|l[åa]ngtidstest|[äa]gare|rapport|efter \d+ ?(år|mil)/i,
    weight: 70,
  },
  {
    href: "/tester",
    label: "Våra tester",
    note: "Modeller vi kört och mätt själva",
    test: /test|k[öo]rintryck|j[äa]mf[öo]r|omd[öo]me|recension/i,
    weight: 72,
  },
  {
    href: "/jamfor",
    label: "Jämför modeller",
    note: "Mått, vikt och pris sida vid sida",
    test: /j[äa]mf[öo]r|skillnad|mot|vs|alternativ|v[äa]lja mellan/i,
    weight: 76,
  },
  {
    href: "/husbil-matchning",
    label: "Husbilsmatchning",
    note: "6 frågor → rätt planlösning för er",
    test: /v[äa]lja|planl[öo]sning|familj|f[öo]rsta husbil|nyb[öo]rjare|passar/i,
    weight: 80,
  },
  {
    href: "/ordlista",
    label: "Husbilsordlistan",
    note: "Alla begrepp förklarade på svenska",
    test: /begrepp|betyder|ordlista|terminologi|f[öo]rkortning|alkov|halvintegrerad/i,
    weight: 66,
  },
  {
    href: "/rapido",
    label: "Rapido",
    note: "Modeller, prislista och delar – vi är återförsäljare",
    test: /rapido|fransk|integrerad|premium/i,
    weight: 78,
  },
  {
    href: "/omdomen",
    label: "Omdömen",
    note: "Vad kunder tycker om oss",
    test: /omd[öo]me|kund|f[öo]rtroende|trygg|rekommend/i,
    weight: 60,
  },
];

/**
 * Väljer relaterade länkar för en sida.
 *
 * @param key   Stabil nyckel (t.ex. slug) — styr deterministisk variation.
 * @param text  Sidans text: titel + nyckelord + brödtext.
 * @param opts  currentHref utesluts alltid; max styr antalet länkar.
 */
export function pickRelatedLinks(
  key: string,
  text: string,
  opts: { currentHref?: string; max?: number } = {},
): RelatedLink[] {
  const max = opts.max ?? 6;
  const current = (opts.currentHref ?? "").replace(/\/+$/, "");
  const seed = hashString(key || "x");

  const isSelf = (href: string) =>
    !!current && (href === current || current.startsWith(`${href}/`));

  const matched = RULES.filter((r) => !isSelf(r.href) && r.test.test(text))
    // Liten deterministisk jitter så syskonsidor inte får identiska listor.
    .map((r, i) => ({ ...r, score: r.weight + (((seed >> (i % 12)) & 7) * 2) }))
    .sort((a, b) => b.score - a.score)
    .map(({ href, label, note }) => ({ href, label, note }));

  const out: RelatedLink[] = [];
  const seen = new Set<string>();
  for (const l of [...matched, ...EVERGREEN.filter((l) => !isSelf(l.href))]) {
    if (seen.has(l.href)) continue;
    seen.add(l.href);
    out.push(l);
    if (out.length >= max) break;
  }
  return out;
}
